import { Box, Flex } from '@chakra-ui/react';
import { InputProps } from '@copilotkit/react-ui';
import { Navigate } from 'react-router';
import {
  CopilotChat,
  CopilotKit,
} from '~/apps/consumer/components/CopilotChat';
import { CopilotChatFloatingInput } from '~/apps/consumer/components/CopilotChat/CopilotChatFloatingInput';
import { SIDEBAR_WIDTH_COLLAPSED } from '~/apps/consumer/components/PageDetailLayout/SidebarItem';
import { CenterSpinner } from '~/common/components/CenterSpinner';
import { useProdFeatureFlagEnabled } from '~/common/hooks/useProdFeatureFlagEnabled';
import { useAppContext } from '~/services/main/contexts/AppContext';
import { ArtifactsSidebar } from './components/ArtifactsSidebar';
import { ConversationsSidebar } from './components/ConversationsSidebar';
import {
  DataCoachChatProvider,
  useDataCoachChat,
} from './DataCoachChatContext';

const FloatingInput = (props: InputProps) => (
  <Box
    bottom="30px"
    left={0}
    maxW="800px"
    mx="auto"
    position="absolute"
    right={0}
    zIndex={5}
  >
    <CopilotChatFloatingInput autoFocus {...props} />
  </Box>
);

const DataCoachChatContent = () => {
  const { selectedConversation } = useDataCoachChat();

  return (
    <Flex
      bg="bg.mainDark"
      height="100dvh"
      width={`calc(100dvw - ${SIDEBAR_WIDTH_COLLAPSED})`}
    >
      <ConversationsSidebar />
      <Flex
        direction="column"
        flex={1}
        h="100%"
        overflow="hidden"
        position="relative"
        sx={{ '& > *': { flex: 1, minH: 0 } }}
      >
        <CopilotChat fullWidth Input={FloatingInput} title="Data Coach AI" />
      </Flex>
      {selectedConversation && <ArtifactsSidebar />}
    </Flex>
  );
};

export const DataCoachChatPage = () => {
  const {
    selectedOrganization,
    loadingOrganizations,
    loadingIsSelectedOrgAdmin,
    isSelectedOrgAdmin,
  } = useAppContext();

  const isEssentialsEnabled = useProdFeatureFlagEnabled('essentials-product');
  const isDataCoachEnabled = useProdFeatureFlagEnabled('data-coach-chat');

  if (loadingOrganizations || loadingIsSelectedOrgAdmin) {
    return <CenterSpinner />;
  }

  if (
    !isEssentialsEnabled ||
    !isDataCoachEnabled ||
    !selectedOrganization ||
    !isSelectedOrgAdmin
  ) {
    return <Navigate replace to="/" />;
  }

  return (
    <Box bg="bg.mainDark" overflow="hidden">
      <CopilotKit agent="indigo-ai" runtimeUrl="/copilot/agent">
        <DataCoachChatProvider>
          <DataCoachChatContent />
        </DataCoachChatProvider>
      </CopilotKit>
    </Box>
  );
};
